import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Modal, Input } from 'semantic-ui-react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AppPrimaryButton, AppButton } from 'elements'
import { FlexRowBetween, TitleText } from 'components'
import { X } from 'phosphor-react'
import { InspectionService } from 'utility/services/inspection'
import { buildSignInUrlWithReturn } from 'utility/auth-redirect'
import {
    chipLabel,
    filterEmailSuggestions,
    isPartner,
    isValidEmail,
    normalizeRecipients,
    sendErrorMessage,
    sourceLabel,
    topRecentSuggestions,
} from 'utility/email-recipients'
import { useNotification } from 'hooks/toast'
import styled from 'styled-components'

const Hint = styled.p`
    display: block;
    clear: both;
    margin: 10px 0 0;
    color: #6b7280;
    font-size: 13px;
    line-height: 1.45;
`

const FieldLabel = styled.label`
    display: block;
    margin-bottom: 6px;
    font-weight: 600;
    color: #1f2430;
    font-size: 14px;
`

const FieldWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
`

const InputRow = styled.div`
    display: block;
    width: 100%;
    overflow: hidden;

    .ui.input {
        width: 100%;
    }
`

const ChipList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
`

const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 100%;
    padding: 4px 6px 4px 10px;
    border-radius: 999px;
    background: #eef1fb;
    color: #1f2430;
    font-size: 13px;
    line-height: 1.3;
`

const ChipText = styled.span`
    overflow-wrap: anywhere;
`

const ChipRemove = styled.button`
    border: 0;
    background: transparent;
    color: #4b5563;
    width: 20px;
    height: 20px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;

    &:hover {
        background: #dbe1f6;
    }
`

const SuggestBox = styled.div`
    margin-top: 8px;
    background: #ffffff;
    border: 1px solid #e6e8ef;
    border-radius: 10px;
    max-height: 280px;
    overflow-y: auto;
`

const GroupLabel = styled.div`
    padding: 8px 12px 4px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #6b7280;
`

const SuggestRow = styled.button`
    width: 100%;
    border: 0;
    background: ${props => (props.$active ? '#eef1fb' : 'transparent')};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 12px;
    text-align: left;
    cursor: pointer;
    font-family: inherit;
`

const SuggestText = styled.span`
    min-width: 0;
    display: flex;
    flex-direction: column;
    overflow-wrap: anywhere;
    color: #1f2430;
    font-size: 14px;
`

const SuggestName = styled.span`
    color: #1f2430;
    font-size: 14px;
    font-weight: 600;
`

const SuggestEmail = styled.span`
    color: ${props => (props.$primary ? '#1f2430' : '#6b7280')};
    font-size: ${props => (props.$primary ? '14px' : '12px')};
`

const SourceTag = styled.span`
    flex-shrink: 0;
    border-radius: 999px;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 700;
    background: ${props => (props.$partner ? '#e6f7f4' : '#eef1fb')};
    color: ${props => (props.$partner ? '#0f766e' : '#263ba6')};
`

export const CustomerEmailModal = ({ open, onClose, reportId }) => {
    const navigate = useNavigate()
    const location = useLocation()
    const { successToast, errorToast } = useNotification()
    const inputRef = useRef(null)
    const [recipients, setRecipients] = useState([])
    const [chips, setChips] = useState([])
    const [emailInput, setEmailInput] = useState('')
    const [activeIndex, setActiveIndex] = useState(0)
    const [listClosed, setListClosed] = useState(false)
    const [sending, setSending] = useState(false)
    const query = emailInput.trim()

    const suggestions = useMemo(() => {
        if (query) return filterEmailSuggestions(recipients, query, chips)
        return topRecentSuggestions(recipients, chips, 20)
    }, [recipients, query, chips])
    const safeIndex = suggestions.length
        ? Math.min(activeIndex, suggestions.length - 1)
        : 0
    const suggestionsOpen = suggestions.length > 0 && (!query || !listClosed)
    const hasPartners = recipients.some(item => isPartner(item.source))

    useEffect(() => {
        if (!open) return undefined
        setChips([])
        setEmailInput('')
        setActiveIndex(0)
        setListClosed(false)
        setRecipients([])
        let cancelled = false
        const load = async () => {
            try {
                const result = await InspectionService.getEmailRecipients(20)
                if (!cancelled) setRecipients(normalizeRecipients(result))
            } catch (err) {
                if (cancelled) return
                if (err?.status === 401) {
                    navigate(
                        buildSignInUrlWithReturn(
                            location.pathname,
                            location.search,
                        ),
                    )
                }
            }
        }
        load()
        const focusTimer = setTimeout(() => inputRef.current?.focus(), 80)
        return () => {
            cancelled = true
            clearTimeout(focusTimer)
        }
    }, [open, navigate, location.pathname, location.search])

    useEffect(() => {
        setActiveIndex(0)
        setListClosed(false)
    }, [emailInput])

    const addChip = (chip, { keepQuery = false } = {}) => {
        const email = String(chip?.email || '').trim()
        if (!email) return
        if (chips.some(item => item.email.toLowerCase() === email.toLowerCase())) {
            if (!keepQuery) setEmailInput('')
            return
        }
        setChips(current => [
            ...current,
            {
                email,
                name: chip?.name || '',
                source: chip?.source || '',
            },
        ])
        if (!keepQuery) {
            setEmailInput('')
            setActiveIndex(0)
        }
    }

    const addTypedEmail = () => {
        const trimmed = emailInput.trim()
        if (!trimmed) return
        if (!isValidEmail(trimmed)) {
            errorToast('Please enter a valid email address')
            return
        }
        if (chips.some(item => item.email.toLowerCase() === trimmed.toLowerCase())) {
            errorToast('This email address is already added')
            return
        }
        const known = recipients.find(
            item => item.email.toLowerCase() === trimmed.toLowerCase(),
        )
        addChip(known || { email: trimmed })
    }

    const onInputKeyDown = event => {
        if (event.key === 'ArrowDown' && suggestionsOpen) {
            event.preventDefault()
            setActiveIndex(index =>
                Math.min(index + 1, Math.max(suggestions.length - 1, 0)),
            )
            return
        }
        if (event.key === 'ArrowUp' && suggestionsOpen) {
            event.preventDefault()
            setActiveIndex(index => Math.max(index - 1, 0))
            return
        }
        if (event.key === 'Enter') {
            event.preventDefault()
            if (suggestionsOpen) addChip(suggestions[safeIndex], { keepQuery: true })
            else addTypedEmail()
            return
        }
        if (event.key === 'Escape' && suggestionsOpen) {
            event.preventDefault()
            event.stopPropagation()
            setListClosed(true)
            return
        }
        if (event.key === 'Backspace' && !emailInput && chips.length) {
            setChips(current => current.slice(0, -1))
        }
    }

    const handleSend = async () => {
        if (!chips.length || sending) return
        if (!reportId) {
            errorToast('Report ID is missing')
            return
        }
        setSending(true)
        try {
            const result = await InspectionService.shareReportByEmails({
                ReportId: reportId,
                OtherEmails: chips.map(chip => chip.email).join(';'),
            })
            if (result && (result.Success === false || result.success === false)) {
                errorToast(sendErrorMessage({ error: result, status: 400 }))
                return
            }
            successToast(
                `Email sent successfully to ${chips.length} email address(es)`,
            )
            onClose(true)
        } catch (err) {
            if (err?.status === 401) {
                navigate(
                    buildSignInUrlWithReturn(location.pathname, location.search),
                )
                return
            }
            errorToast(sendErrorMessage(err))
        } finally {
            setSending(false)
        }
    }

    const handleClose = () => {
        if (sending) return
        onClose(false)
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            size="small"
            className="model"
            style={{ width: '560px', maxWidth: 'calc(100vw - 32px)' }}
            closeOnEscape={!suggestionsOpen}
        >
            <Modal.Header>
                <FlexRowBetween>
                    <TitleText>Send Email</TitleText>
                    <X className="pointer" size={32} onClick={handleClose} />
                </FlexRowBetween>
            </Modal.Header>
            <Modal.Content>
                <FieldWrap>
                    <FieldLabel htmlFor="send-email-address">
                        Email Addresses
                    </FieldLabel>
                    <InputRow>
                        <Input
                            ref={inputRef}
                            id="send-email-address"
                            fluid
                            placeholder="Type a name or email address"
                            value={emailInput}
                            autoComplete="off"
                            spellCheck={false}
                            role="combobox"
                            aria-autocomplete="list"
                            aria-expanded={suggestionsOpen}
                            aria-controls="send-email-suggestions"
                            onChange={event => setEmailInput(event.target.value)}
                            onKeyDown={onInputKeyDown}
                            onFocus={() => setListClosed(false)}
                            disabled={sending}
                            action={{
                                content: 'Add',
                                onClick: addTypedEmail,
                                disabled: sending,
                                type: 'button',
                            }}
                        />
                    </InputRow>
                    {chips.length > 0 && (
                        <ChipList>
                            {chips.map(chip => (
                                <Chip key={chip.email} title={chip.email}>
                                    <ChipText>{chipLabel(chip)}</ChipText>
                                    <ChipRemove
                                        type="button"
                                        aria-label={`Remove ${chip.email}`}
                                        onClick={() =>
                                            setChips(current =>
                                                current.filter(
                                                    item => item.email !== chip.email,
                                                ),
                                            )
                                        }
                                    >
                                        <X size={12} />
                                    </ChipRemove>
                                </Chip>
                            ))}
                        </ChipList>
                    )}
                    {suggestionsOpen && (
                        <SuggestBox id="send-email-suggestions" role="listbox">
                            {suggestions.map((item, index) => {
                                const group = sourceLabel(item.source)
                                const previous = suggestions[index - 1]
                                const showGroup =
                                    !previous ||
                                    sourceLabel(previous.source) !== group
                                const showName =
                                    item.name &&
                                    item.name.toLowerCase() !==
                                        item.email.toLowerCase()
                                return (
                                    <React.Fragment key={`${item.source}-${item.email}`}>
                                        {showGroup && <GroupLabel>{group}</GroupLabel>}
                                        <SuggestRow
                                            type="button"
                                            role="option"
                                            aria-selected={index === safeIndex}
                                            $active={index === safeIndex}
                                            title={item.email}
                                            onMouseDown={event => event.preventDefault()}
                                            onMouseEnter={() => setActiveIndex(index)}
                                            onClick={() => addChip(item, { keepQuery: true })}
                                        >
                                            <SuggestText>
                                                {showName && (
                                                    <SuggestName>{item.name}</SuggestName>
                                                )}
                                                <SuggestEmail $primary={!showName}>
                                                    {item.email}
                                                </SuggestEmail>
                                            </SuggestText>
                                            <SourceTag $partner={isPartner(item.source)}>
                                                {group}
                                            </SourceTag>
                                        </SuggestRow>
                                    </React.Fragment>
                                )
                            })}
                        </SuggestBox>
                    )}
                </FieldWrap>
                <Hint>
                    {hasPartners
                        ? 'Pick a previous email, or start typing to see recent recipients and Cintas partners. Press Enter or Add for any other email.'
                        : 'Pick a previous email, or type a name or address. Press Enter or Add for any other email.'}
                </Hint>
            </Modal.Content>
            <Modal.Actions>
                <AppButton type="button" onClick={handleClose} disabled={sending}>
                    Cancel
                </AppButton>
                <AppPrimaryButton
                    type="button"
                    onClick={handleSend}
                    loading={sending}
                    disabled={sending || chips.length === 0}
                >
                    Send Email
                </AppPrimaryButton>
            </Modal.Actions>
        </Modal>
    )
}
